import type { Me } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useRef, type ReactElement } from 'react';
import { DoneForYouCard, HOME_DAYS } from '../acting-for/DoneForYouCard';
import { doneToday } from '../acting-for/doneWords';
import { WaitingOnOthersCard } from '../acting-for/WaitingOnOthersCard';
import { homeQuery, meQuery, waitingQuery } from '../api';
import { doneForMeQuery, waitingOnOthersQuery } from '../api/actingFor';
import { registerQuery } from '../api/insights';
import { getSpotConfig } from '../api/spot';
import { canOpenArea } from '../settings/nav';
import { Hero, PageBody, type HeroAction, type PageHelp } from '../shell';
import { WaitingOnYou } from '../ui';
import { EmployeeHome } from './EmployeeHome';
import { ExecutivePanel } from './ExecutivePanel';
import { edbFyiNotes } from './executiveFyi';
import { FinanceHome } from './FinanceHome';
import {
  HOW_IT_WORKS_HREF,
  MY_ENTRIES_HREF,
  REQUEST_HOW_HREF,
  homeHero,
  recognizeAction,
} from './hero';
import { financeOnly } from './layout';
import { homeModel } from './model';
import { MyRecognitionPanel } from './MyRecognitionPanel';
import { QueueHome } from './QueueHome';
import { StockAdminPoolsCard } from './StockAdminPoolsCard';
import { seesSpotInProgress, SpotInProgressCard } from './SpotInProgressCard';
import { RequesterPanel } from './RequesterPanel';
import { SectorPanel } from './SectorPanel';
import { useDismissStep } from './useDismissStep';
import { useHomeView } from './view';
import { useWaitingOrder } from './useWaitingOrder';
import { electionFyi } from './electionFyi';
import { toWaitingItem } from './waiting';
import s from './Home.module.css';

/** Help topics for Home, by the person's role mix. */
export function homeHelp(me: Me): PageHelp {
  const roles = new Set(me.roles.map((a) => a.role));
  if (roles.has('stock_admin')) {
    return {
      pageLabel: 'Home · Stock Admin',
      // APP-232: Spot Award nominations describes Home's Spot awards in progress.
      topicIds: ['queue', 'waiting', 'act-for-others', 'in-progress', 'spot'],
    };
  }
  if (roles.has('executive')) {
    return {
      pageLabel: 'Home · Executive',
      topicIds: [
        'executive-approvals',
        'over-budget',
        'act-for-others',
        'pool',
        'budget-ledger',
        'waiting',
      ],
    };
  }
  if (roles.has('sector_lead') || roles.has('delegate')) {
    return {
      pageLabel: roles.has('sector_lead') ? 'Home · Sector Lead' : 'Home · Delegate',
      topicIds: [
        'pool',
        'in-progress',
        'delegate-entry',
        // APP-148: a Sector Lead's Done for you; a delegate has none.
        ...(roles.has('sector_lead') ? ['act-for-others'] : []),
        'team-snapshot',
        'waiting',
      ],
    };
  }
  if (roles.has('executive_delegate')) {
    return {
      pageLabel: 'Home · Executive delegate',
      topicIds: ['executive-bonus', 'requesters', 'in-progress', 'waiting'],
    };
  }
  if (financeOnly(me.roles)) {
    return {
      pageLabel: 'Home · Finance',
      topicIds: ['waiting', 'pool', 'paid-from', 'charge-codes', 'budget-ledger'],
    };
  }
  return { pageLabel: 'Home', topicIds: ['waiting', 'election', 'letters', 'spot'] };
}

/**
 * The blended Home: one page built from every role the person holds. Waiting on you tops it
 * (all roles merged, oldest first), then the role panels, then My recognition, always last.
 * Stock Admins open on their queue instead, with Waiting on you folded above it.
 */
export function HomePage(): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const view = useHomeView();
  const { data: waiting } = useSuspenseQuery(waitingQuery);
  const { data } = useSuspenseQuery(homeQuery(me, view));
  const model = useMemo(() => homeModel(me, waiting, data, view), [me, waiting, data, view]);
  const spotConfig = useQuery({
    queryKey: ['spot', 'config'],
    queryFn: ({ signal }) => getSpotConfig(signal),
    enabled: model.layout === 'employee' && model.nothingYet,
    staleTime: 0,
  });
  const howItWorksRef = useRef<HTMLHeadingElement>(null);
  const requestHowRef = useRef<HTMLHeadingElement>(null);
  const myEntriesRef = useRef<HTMLHeadingElement>(null);

  // APP-148 D2: a Stock Admin's Waiting on others (404 for anyone else), while a switch is on;
  // an Executive's or Sector Lead's Done for you. Neither is read in sample-roles Preview.
  const live = me.preview === null;
  const roles = new Set(me.roles.map((a) => a.role));
  const othersRead = useQuery({
    ...waitingOnOthersQuery,
    enabled: live && roles.has('stock_admin'),
    retry: false,
  });
  const others =
    othersRead.data !== undefined &&
    (othersRead.data.switches.executives || othersRead.data.switches.sectorLeads)
      ? othersRead.data
      : null;
  // R82: a Stock Admin's Budget pools, live only, as the cards above: sample-roles Preview draws
  // Home's sample instead, whose Budget pools card stands in for it (R11).
  const livePools = live ? data.positions : null;
  const doneFor = model.executive !== null || model.sectors.some((p) => p.role === 'sector_lead');
  const doneRead = useQuery({
    ...doneForMeQuery({ days: HOME_DAYS }),
    enabled: live && doneFor,
    retry: false,
  });
  const today = useMemo(
    () => (doneRead.data === undefined ? null : doneToday(doneRead.data.items, model.asOf)),
    [doneRead.data, model.asOf],
  );

  const hero = useMemo(
    () => homeHero(me, model, new Date(), { others, doneToday: today }),
    [me, model, others, today],
  );
  // The in-page actions ("How recognition works", "How it works") scroll to their card.
  const inPage: Record<string, typeof howItWorksRef> = {
    [HOW_IT_WORKS_HREF]: howItWorksRef,
    [REQUEST_HOW_HREF]: requestHowRef,
    [MY_ENTRIES_HREF]: myEntriesRef,
  };
  const actions = hero.actions?.map((action): HeroAction => {
    const target = action.href === undefined ? undefined : inPage[action.href];
    if (target === undefined) return action;
    return {
      label: action.label,
      onPress: () => {
        target.current?.scrollIntoView({ block: 'start' });
        target.current?.focus({ preventScroll: true });
      },
      ...(action.tone && { tone: action.tone }),
    };
  }) as typeof hero.actions;

  const waitingItems = useMemo(
    () => model.waiting.map((item) => toWaitingItem(item, model.asOf, me.person.id)),
    [model, me.person.id],
  );
  const dismiss = useDismissStep(waiting);
  const waitingOrder = useWaitingOrder();
  // X1: Executive Discretionary Bonuses the Executives decide outside the app, as notes under
  // Waiting on you (nothing to do here), and that everything in it also goes to their group's inbox.
  const executive = model.executive !== null;
  const outside = useQuery({
    ...registerQuery({
      period: 'any_time',
      type: ['EDB'],
      stage: ['approval'],
      sort: 'oldest',
      size: 20,
    }),
    enabled: executive && view === 'live',
  });
  // E1: on the employee Home with no election open, a note that there's nothing to decide and
  // the way to preview the calculator (APP-093).
  const electionNote =
    model.layout === 'employee' && !model.waiting.some((w) => w.type === 'make_election');
  const windowDays = model.mine.electionWindowDays;
  const fyi = useMemo(
    () => [
      ...(executive ? edbFyiNotes(outside.data?.rows ?? [], me.person.id) : []),
      ...(electionNote ? [electionFyi(windowDays)] : []),
    ],
    [executive, outside.data, me.person.id, electionNote, windowDays],
  );
  const waitingCard = (
    <WaitingOnYou
      items={waitingItems}
      fyi={fyi}
      {...(executive &&
        // APP-092: only when approvals really go to the group address now.
        waiting.executiveGroupEmail === true && { note: 'Also emailed to the Executive group' })}
      defaultCollapsed={model.layout === 'queue'}
      defaultOrder={waitingOrder}
      onDismiss={dismiss}
    />
  );
  const recognize = recognizeAction(model);
  // Requests whose decision waits on this person, and where to record it (R1).
  const decisions = useMemo(
    () =>
      new Map(
        model.waiting.flatMap((w) =>
          w.type === 'record_decision' &&
          (w.role === 'requester' || w.role === 'executive_delegate')
            ? [[w.award.id, `/approvals/${w.award.id}`] as const]
            : [],
        ),
      ),
    [model],
  );

  // APP-232 D3: Spot awards in progress, under Waiting on you, for whoever reads every row of All
  // Spot awards (the committee, the Stock Admin team, System Admins), live only.
  const spotCard = seesSpotInProgress(me) ? <SpotInProgressCard /> : null;

  // F1: Finance's Home is its own greeting over the Spend dashboard, under Waiting on you.
  if (model.layout === 'finance')
    return <FinanceHome me={me} waiting={waitingCard} spot={spotCard} />;

  return (
    <>
      <Hero
        // A new person or view starts with their default header again.
        key={`${me.person.id}-${view}`}
        eyebrow={hero.eyebrow}
        eyebrowTone={hero.eyebrowTone}
        title={hero.title}
        // The heading is a greeting; the browser tab says which page this is (QA-022).
        documentTitle="Home"
        sub={hero.sub}
        {...(actions && { actions })}
        stats={hero.stats}
        photos={hero.photos}
        titleTestId="home-title"
      />
      <PageBody>
        {model.layout === 'employee' ? (
          <EmployeeHome
            me={me}
            mine={model.mine}
            spotApprovalMode={spotConfig.data?.approvalMode}
            spotNomination={model.options.spotNomination}
            nothingYet={model.nothingYet}
            waiting={waitingCard}
            spot={spotCard}
            howItWorksRef={howItWorksRef}
          />
        ) : (
          <div className={s.stack}>
            {model.queue ? (
              <QueueHome
                queue={model.queue}
                meId={me.person.id}
                asOf={model.asOf}
                waiting={waitingCard}
                {...((others !== null || othersRead.isError) && {
                  others: <WaitingOnOthersCard others={others} asOf={model.asOf} />,
                })}
                {...(livePools !== null && {
                  pools: (
                    <StockAdminPoolsCard positions={livePools} budgetsHref={budgetsHrefFor(me)} />
                  ),
                })}
                history={model.queueHistory}
                spot={spotCard}
              />
            ) : (
              <>
                {waitingCard}
                {spotCard}
              </>
            )}
            {doneFor && live && <DoneForYouCard meId={me.person.id} />}
            {model.executive && (
              <ExecutivePanel
                ex={model.executive}
                firstRun={view === 'first-run'}
                meId={me.person.id}
                asOf={model.asOf}
              />
            )}
            {model.sectors.map((panel) => (
              <SectorPanel
                key={panel.sector.id}
                panel={panel}
                insights={me.nav.includes('insights')}
                myName={me.person.displayName}
                meId={me.person.id}
                asOf={model.asOf}
                {...(panel.role === 'delegate' && { entriesRef: myEntriesRef })}
              />
            ))}
            {model.requester && (
              <RequesterPanel
                r={model.requester}
                decisions={decisions}
                howItWorksRef={requestHowRef}
              />
            )}
            {model.layout === 'blended' && (
              <MyRecognitionPanel
                mine={model.mine}
                asOf={model.asOf}
                nothingYet={model.nothingYet}
                recognizeHref={recognize?.href ?? null}
              />
            )}
          </div>
        )}
      </PageBody>
    </>
  );
}

/**
 * Where a reader sees the budgets from Home's Budget pools: an Executive's Budget pools, else
 * Settings › Budgets for those who read it (a Stock Admin), else nowhere.
 */
function budgetsHrefFor(me: Me): string | null {
  if (me.roles.some((a) => a.role === 'executive')) return '/manage/executive/budget-pools';
  return canOpenArea(me, 'budgets') ? '/settings/budgets' : null;
}
